import React from "react";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import {
  convertTime,
  formatDateToLocal,
} from "../../../utils/dateAndTimeConversion";
import { getStatusBadge, getStatusText } from "../../admin/VehiclesTable";
import { FaWheelchair } from "react-icons/fa6";

const ParentReservationModal = ({ show, onClose, instance }) => {
  const { t } = useTranslation();

  if (!instance) return null;

  const {
    instance: instanceData,
    reservation,
    school,
    vendor,
    driver,
    vehicle,
    route,
    my_students = [],
  } = instance;

  // Helper function to format text
  const formatText = (text) => {
    if (!text) return "N/A";
    return text.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="lg"
        centered
        backdrop="static"
        className="detailModel"
      >
        <Modal.Header closeButton className="border-0 position-relative">
          <Modal.Title className="w-100 text-center">
            <h4 className="mb-0 fw-bold text-white">
              <i className="fas fa-bus me-2"></i>
              {t("reservation")} {t("details")}
            </h4>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="pt-0"
          style={{
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <div className="reservation-details-container">
            {/* Header Card with Status */}
            <div className="header-card mb-4 mt-2">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="reservation-id">
                    <span className="badge bg-light text-dark fs-6">
                      <i className="fas fa-hashtag me-2"></i>
                      {t("instance")} {t("ID")}:{" "}
                      <strong>{instanceData?.id || "N/A"}</strong>
                    </span>
                    <span className="badge bg-info text-dark ms-2 fs-6">
                      <i className="fas fa-ticket me-2"></i>
                      {t("reservation")} {t("ID")}:{" "}
                      <strong>{reservation?.id || "N/A"}</strong>
                    </span>
                  </div>
                </div>
                <div>
                  <span className={getStatusBadge(instanceData?.status)}>
                    {getStatusText(instanceData?.status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Main Information Grid */}
            <div className="info-grid mb-4">
              <div className="row">
                {/* Left Column */}
                <div className="col-md-6">
                  {/* Instance Details */}
                  <div className="info-card mb-3">
                    <div className="info-header">
                      <i className="fas fa-calendar-alt me-2 text-primary"></i>
                      <h6 className="mb-0 fw-bold">
                        {t("instance")} {t("information")}
                      </h6>
                    </div>
                    <div className="info-body">
                      <div className="info-item">
                        <span className="info-label">
                          <i className="fas fa-calendar me-2"></i>
                          {t("date")}:
                        </span>
                        <span className="info-value">
                          {formatDateToLocal(instanceData?.date)}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">
                          <i className="fas fa-clock me-2"></i>
                          {t("morning")} {t("start")} {t("time")}:
                        </span>
                        <span className="info-value">
                          {reservation?.start_time
                            ? convertTime(reservation.start_time)
                            : "N/A"}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">
                          <i className="fas fa-clock me-2"></i>
                          {t("morning")} {t("end")} {t("time")}:
                        </span>
                        <span className="info-value">
                          {reservation?.end_time
                            ? convertTime(reservation.end_time)
                            : "N/A"}
                        </span>
                      </div>
                      {reservation?.return_start_time && (
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-clock me-2"></i>
                            {t("afternoon")} {t("end")} {t("time")}:
                          </span>
                          <span className="info-value">
                            {convertTime(reservation.return_start_time)}
                          </span>
                        </div>
                      )}
                      {reservation?.return_end_time && (
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-clock me-2"></i>
                            {t("afternoon")} {t("end")} {t("time")}:
                          </span>
                          <span className="info-value">
                            {convertTime(reservation.return_end_time)}
                          </span>
                        </div>
                      )}
                      {/* {instanceData?.actual_start_time && (
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-clock me-2"></i>
                            Actual Start Time:
                          </span>
                          <span className="info-value">
                            {convertTime(instanceData.actual_start_time)}
                          </span>
                        </div>
                      )}
                      {instanceData?.actual_end_time && (
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-clock me-2"></i>
                            Actual End Time:
                          </span>
                          <span className="info-value">
                            {convertTime(instanceData.actual_end_time)}
                          </span>
                        </div>
                      )} */}
                      <div className="info-item">
                        <span className="info-label">
                          <i className="fas fa-chair me-2"></i>
                          {t("available")} {t("seats")}:
                        </span>
                        <span className="info-value">
                          {instanceData?.available_seats || 0}
                        </span>
                      </div>
                      {instanceData?.trip_leg && (
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-exchange-alt me-2"></i>
                            Trip Leg:
                          </span>
                          <span className="info-value">
                            <span
                              className={`badge ${instanceData.trip_leg === "return" ? "bg-warning text-dark" : "bg-success"}`}
                            >
                              {instanceData.trip_leg === "return"
                                ? "School to Home"
                                : "Home to School"}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reservation Details */}
                  <div className="info-card mb-3">
                    <div className="info-header">
                      <i className="fas fa-ticket-alt me-2 text-primary"></i>
                      <h6 className="mb-0 fw-bold">
                        {t("reservation")} {t("information")}
                      </h6>
                    </div>
                    <div className="info-body">
                      <div className="info-item">
                        <span className="info-label">
                          <i className="fas fa-tag me-2"></i>
                          {t("booking")} {t("type")}:
                        </span>
                        <span className="info-value">
                          {formatText(reservation?.booking_type)}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">
                          <i className="fas fa-subscript me-2"></i>
                          {t("subscription")} {t("type")}:
                        </span>
                        <span className="info-value">
                          {formatText(reservation?.subscription_type)}
                        </span>
                      </div>
                      {reservation?.charter_frequency && (
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-redo me-2"></i>
                            {t("charter")} {t("frequency")}:
                          </span>
                          <span className="info-value">
                            {formatText(reservation.charter_frequency)}
                          </span>
                        </div>
                      )}
                      {reservation?.charter_type && (
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-bus me-2"></i>
                            {t("charter")} {t("type")}:
                          </span>
                          <span className="info-value">
                            {formatText(reservation.charter_type)}
                          </span>
                        </div>
                      )}
                      <div className="info-item">
                        <span className="info-label">
                          <i className="fas fa-info-circle me-2"></i>
                          {t("reservation")} {t("status")}:
                        </span>
                        <span className="info-value">
                          {formatText(reservation?.status)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* School Information */}
                  {school && (
                    <div className="info-card mb-3">
                      <div className="info-header">
                        <i className="fas fa-school me-2 text-primary"></i>
                        <h6 className="mb-0 fw-bold">
                          {t("school")} {t("information")}
                        </h6>
                      </div>
                      <div className="info-body">
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-building me-2"></i>
                            {t("name")}:
                          </span>
                          <span className="info-value">{school?.name}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-envelope me-2"></i>
                            {t("email")}:
                          </span>
                          <span className="info-value">{school?.email}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-briefcase me-2"></i>
                            {t("company")} {t("name")}:
                          </span>
                          <span className="info-value">
                            {school?.company_name}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-map-marker-alt me-2"></i>
                            {t("address")}:
                          </span>
                          <span className="info-value">{school?.address}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Vendor Information */}
                  {vendor && (
                    <div className="info-card mb-3">
                      <div className="info-header">
                        <i className="fas fa-store me-2 text-primary"></i>
                        <h6 className="mb-0 fw-bold">
                          {t("vendor")} {t("information")}
                        </h6>
                      </div>
                      <div className="info-body">
                        {vendor?.name && (
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-building me-2"></i>
                              {t("name")}:
                            </span>
                            <span className="info-value">
                              {vendor?.name || "N/A"}
                            </span>
                          </div>
                        )}

                        {vendor?.company_name && (
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-building me-2"></i>
                              {t("company")} {t("name")}:
                            </span>
                            <span className="info-value">
                              {vendor?.company_name || "N/A"}
                            </span>
                          </div>
                        )}
                        {vendor?.email && (
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-envelope me-2"></i>
                              {t("email")}:
                            </span>
                            <span className="info-value">{vendor?.email}</span>
                          </div>
                        )}
                        {vendor?.address && (
                          <div className="info-item">
                            <span className="info-label">
                              <i className="fas fa-map-marker-alt me-2"></i>
                              {t("address")}:
                            </span>
                            <span className="info-value">
                              {vendor?.address}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="col-md-6">
                  {/* Driver Information */}
                  {driver && (
                    <div className="info-card mb-3">
                      <div className="info-header">
                        <i className="fas fa-user-tie me-2 text-primary"></i>
                        <h6 className="mb-0 fw-bold">
                          {t("driver")} {t("information")}
                        </h6>
                      </div>
                      <div className="info-body">
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-user me-2"></i>
                            {t("name")}:
                          </span>
                          <span className="info-value">{driver?.name}</span>
                        </div>
                        {/* <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-phone me-2"></i>
                            {t("phone")}:
                          </span>
                          <span className="info-value">{driver?.phone}</span>
                        </div> */}
                        {/* <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-envelope me-2"></i>
                            {t("email")}:
                          </span>
                          <span className="info-value">{driver?.email}</span>
                        </div> */}
                      </div>
                    </div>
                  )}

                  {/* Vehicle Information */}
                  {vehicle && (
                    <div className="info-card mb-3">
                      <div className="info-header">
                        <i className="fas fa-bus me-2 text-primary"></i>
                        <h6 className="mb-0 fw-bold">
                          {t("vehicle")} {t("information")}
                        </h6>
                      </div>
                      <div className="info-body">
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-id-card me-2"></i>
                            {t("license")} {t("number")}:
                          </span>
                          <span className="info-value">
                            {vehicle?.license_number}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-car me-2"></i>
                            {t("type")}:
                          </span>
                          <span className="info-value">{vehicle?.type}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-chair me-2"></i>
                            {t("total")} {t("seats")}:
                          </span>
                          <span className="info-value">{vehicle?.seats}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Route Information */}
                  {route && (
                    <div className="info-card mb-3">
                      <div className="info-header">
                        <i className="fas fa-route me-2 text-primary"></i>
                        <h6 className="mb-0 fw-bold">
                          {t("route")} {t("information")}
                        </h6>
                      </div>
                      <div className="info-body">
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-signature me-2"></i>
                            {t("name")}:
                          </span>
                          <span className="info-value">{route?.name}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-map-pin me-2"></i>
                            {t("pickup")} {t("address")}:
                          </span>
                          <span className="info-value">
                            {route?.pickup_address}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <i className="fas fa-flag-checkered me-2"></i>
                            {t("dropoff")} {t("address")}:
                          </span>
                          <span className="info-value">
                            {route?.dropoff_address}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Students Section */}
            {my_students.length > 0 && (
              <div className="students-section mb-4">
                <div className="info-card">
                  <div className="info-header">
                    <i className="fas fa-users me-2 text-primary"></i>
                    <h6 className="mb-0 fw-bold">
                      {t("students")} ({my_students.length})
                    </h6>
                  </div>
                  <div className="info-body p-0">
                    <div className="students-table-wrap">
                      <table className="students-table">
                        <thead>
                          <tr>
                            <th style={{ width: 40 }}>#</th>
                            <th>
                              {t("student")} {t("name")}
                            </th>
                            <th>
                              {t("roll")} {t("number")}
                            </th>
                            <th>{t("status")}</th>
                            <th>{t("disability")}</th>
                            <th>
                              {t("pickup")} {t("time")}
                            </th>
                            <th>
                              {t("attendance")} {t("status")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {my_students.map((studentItem, index) => (
                            <tr key={studentItem.student_reservation_id}>
                              <td
                                className="text-center text-muted"
                                style={{ fontSize: 13 }}
                              >
                                {index + 1}
                              </td>
                              <td>
                                <div className="student-name-cell">
                                  <div className="student-avatar">
                                    {(studentItem.student?.name ||
                                      "?")[0].toUpperCase()}
                                  </div>
                                  <span className="fw-semibold">
                                    {studentItem.student?.name}
                                  </span>
                                  {studentItem.student?.is_disabled === 1 && (
                                    <span
                                      className="badge bg-warning text-dark ms-1"
                                      style={{ fontSize: 10 }}
                                    >
                                      <FaWheelchair />
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className="roll-badge">
                                  {studentItem.student?.roll_number || "—"}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`status-pill ${studentItem.status === "active" ? "active" : "inactive"}`}
                                >
                                  {studentItem.status}
                                </span>
                              </td>
                              <td style={{ fontSize: 12 }}>
                                {studentItem.student?.is_disabled ? (
                                  <div>
                                    <div className="text-muted">
                                      {studentItem.student?.disability_notes ||
                                        "—"}
                                    </div>
                                    {studentItem.student?.dr_phone_no && (
                                      <div className="text-muted">
                                        <i className="fas fa-phone me-1"></i>
                                        {studentItem.student?.dr_phone_no}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td style={{ fontSize: 13 }}>
                                {studentItem.attendance?.scheduled_pickup_time
                                  ? convertTime(
                                      studentItem.attendance
                                        ?.scheduled_pickup_time,
                                    )
                                  : "—"}
                              </td>
                              <td>
                                <span
                                  className={`status-pill ${
                                    studentItem.attendance?.status === "present"
                                      ? "active"
                                      : studentItem.attendance?.status ===
                                          "scheduled"
                                        ? "scheduled"
                                        : "inactive"
                                  }`}
                                >
                                  {formatText(studentItem.attendance?.status) ||
                                    "—"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer Information */}
            <div className="footer-section">
              <div className="footer-info">
                <div className="row">
                  <div className="col-md-12">
                    <div className="meta-info text-center">
                      <span className="meta-item">
                        <i className="far fa-clock me-2 text-muted"></i>
                        {t("instance")} {t("status")}:{" "}
                        <strong>{formatText(instanceData?.status)}</strong>
                      </span>
                      {/* {instanceData?.assigned_at && (
                        <span className="meta-item ms-3">
                          <i className="far fa-calendar-check me-2 text-muted"></i>
                          {t("assigned")}:{" "}
                          {formatDateToLocal(instanceData.assigned_at)}
                        </span>
                      )} */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0">
          <div className="text-center w-100">
            <Button className="btn btn-primary px-4" onClick={onClose}>
              <i className="fas fa-times me-2"></i>
              {t("btns.close")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      <style>{`
        .detailModel .modal-dialog {
          max-width: 900px;
        }

        .header-card {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 15px 20px;
          border-radius: 10px;
          border-left: 4px solid #f0b342;
        }
        .info-card {
          background: #fff;
          border-radius: 10px;
          border: 1px solid #e0e0e0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .info-header {
          background: #f8f9fa;
          padding: 12px 15px;
          border-bottom: 1px solid #e0e0e0;
          display: flex;
          align-items: center;
        }

        .info-body {
          padding: 15px;
        }

        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px dashed #f0f0f0;
        }

        .info-item:last-child {
          border-bottom: none;
        }

        .info-label {
          color: #666;
          font-size: 14px;
          display: flex;
          align-items: center;
        }

        .info-value {
          color: #333;
          font-weight: 500;
          text-align: right;
          max-width: 60%;
          word-break: break-word;
        }

        .location-card {
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          height: 100%;
        }

        .location-header {
          padding: 12px 15px;
          font-weight: 600;
          font-size: 15px;
          display: flex;
          align-items: center;
        }

        .location-body {
          padding: 15px;
          background: #fff;
          min-height: 80px;
          display: flex;
          align-items: center;
        }

        .location-body p {
          color: #444;
          font-size: 14px;
          line-height: 1.5;
        }

        .notes-card {
          background: #fff;
          border-radius: 10px;
          border: 1px solid #e0e0e0;
          overflow: hidden;
        }

        .notes-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%);
          padding: 12px 15px;
          border-bottom: 1px solid #e0e0e0;
          display: flex;
          align-items: center;
        }

        .notes-body {
          padding: 15px;
          background: #fafafa;
          font-style: italic;
          color: #555;
        }

        .footer-section {
          margin-top: 20px;
          padding-top: 15px;
          border-top: 1px dashed #ddd;
        }

        .meta-info {
          display: flex;
          align-items: center;
        }

        .meta-item {
          font-size: 13px;
          color: #777;
          display: inline-flex;
          align-items: center;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .info-item {
            flex-direction: column;
            align-items: flex-start;
          }

          .info-value {
            text-align: left;
            margin-top: 4px;
            max-width: 100%;
          }

          .location-section .col-md-6 {
            margin-bottom: 15px;
          }
        }

        /* Icon colors */
        .text-primary {
          color: #007bff !important;
        }
        .text-success {
          color: #28a745 !important;
        }
        .text-warning {
          color: #ffc107 !important;
        }
        .text-danger {
          color: #dc3545 !important;
        }
        .text-info {
          color: #17a2b8 !important;
        }

        /* Students Table */
        .students-table-wrap {
          overflow-x: auto;
        }
        .students-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13.5px;
        }
        .students-table thead tr {
          background: #f8f9fa;
        }
        .students-table thead th {
          padding: 10px 14px;
          font-weight: 600;
          color: #444;
          border-bottom: 1px solid #e0e0e0;
          white-space: nowrap;
        }
        .students-table tbody tr {
          border-bottom: 1px solid #f0f0f0;
          transition: background 0.15s;
        }
        .students-table tbody tr:last-child {
          border-bottom: none;
        }
        .students-table tbody tr:hover {
          background: #f8f9fa;
        }
        .students-table td {
          padding: 10px 14px;
          vertical-align: middle;
        }
        .student-name-cell {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .student-avatar {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: #dee2e6;
          color: #495057;
          font-size: 12px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .roll-badge {
          background: #f1f3f5;
          color: #495057;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          font-family: monospace;
        }
        .status-pill {
          padding: 3px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: capitalize;
        }
        .status-pill.active {
          background: #e6f9f0;
          color: #1a9e5c;
        }
        .status-pill.scheduled {
          background: #e8f4fd;
          color: #0c7abf;
        }
        .status-pill.inactive {
          background: #f2f2f2;
          color: #888;
        }
      `}</style>
    </>
  );
};

export default ParentReservationModal;
